import { useMe } from '../../context/MeContext'
import { MeHealthCenter } from '../../api/me'
import { ButtonAnchor, EmptyState } from '../../components/common'
import { trackEvent } from '../../lib/config'
import styles from './HealthCentersPage.module.scss'

const FALLBACK_LOGO_COLORS: readonly [string, string][] = [
  ['#2196f3', '#1565c0'],
  ['#4caf50', '#2e7d32'],
  ['#ff7043', '#d84315'],
  ['#ab47bc', '#6a1b9a'],
  ['#26a69a', '#00695c'],
  ['#fb8c00', '#e65100'],
]

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')
}

function FallbackLogo({ healthCenter: { id, name } }: { healthCenter: MeHealthCenter }) {
  const [light, dark] = FALLBACK_LOGO_COLORS[id % FALLBACK_LOGO_COLORS.length]
  const gradientId = `health-center-logo-${id}`

  return (
    <svg viewBox="0 0 200 200" className={styles.fallbackLogo} role="img" aria-label={name}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={light} />
          <stop offset="1" stopColor={dark} />
        </linearGradient>
      </defs>
      <rect width="200" height="200" rx="44" fill={`url(#${gradientId})`} />
      <path d="M152 16h16v16h16v16h-16v16h-16V48h-16V32h16z" fill="#ffffff" opacity="0.2" />
      <circle cx="30" cy="178" r="56" fill="#ffffff" opacity="0.08" />
      <text
        x="100"
        y="100"
        dy="0.35em"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="76"
        fontWeight="600"
        fontFamily="Roboto, Helvetica, Arial, sans-serif"
      >
        {getInitials(name)}
      </text>
    </svg>
  )
}

function HealthCenterCard({ healthCenter }: { healthCenter: MeHealthCenter }) {
  const patientsPath = `/health_centers/${healthCenter.id}/patients`
  const select = () =>
    trackEvent(
      'Health Center Selected',
      { health_center_id: healthCenter.id, account_type: healthCenter.account_type },
      { transport: 'sendBeacon' },
    )

  return (
    <div className="col-md-4 col-sm-6 col-xs-12">
      <article className={`product-item well ${styles.card}`}>
        <div className="row">
          <div className="col-sm-3">
            <div className={styles.overlay}>
              <div className="row">
                <a href={patientsPath} onClick={select}>
                  {healthCenter.has_logo ? (
                    <img src={healthCenter.logo_url} className={styles.logo} alt={healthCenter.name} />
                  ) : (
                    <FallbackLogo healthCenter={healthCenter} />
                  )}
                </a>
              </div>
            </div>
          </div>
          <div className="col-sm-9">
            <div className="product-body">
              <h3>{healthCenter.name}</h3>
              <div className={styles.buttons}>
                <ButtonAnchor href={patientsPath} onClick={select}>
                  Entrar
                </ButtonAnchor>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  )
}

export default function HealthCentersPage() {
  const me = useMe()

  return (
    <div className="col-md-10 col-md-offset-1" style={{ marginTop: 100 }}>
      <div className={`row ${styles.grid}`}>
        {me.health_centers.length > 0 ? (
          me.health_centers.map((healthCenter) => <HealthCenterCard key={healthCenter.id} healthCenter={healthCenter} />)
        ) : (
          <EmptyState message="No hay centros médicos registrados. Contactanos en contacto@historiaclinica.cl." />
        )}
      </div>
    </div>
  )
}
